<template>
  <div 
    ref="containerRef"
    class="lazy-field-container"
    :class="{ 'is-loading': !isLoaded, 'is-loaded': isLoaded }"
  >
    <!-- Placeholder avant chargement -->
    <div 
      v-if="!isLoaded" 
      class="lazy-field-placeholder input-placeholder"
      @click="loadComponent"
    >
      <div class="placeholder-content">
        <i class="fa fa-edit placeholder-icon"></i>
        <p class="placeholder-text">Cliquer pour charger le champ</p>
        <div v-if="truncatedContent" class="placeholder-preview">
          <div class="preview-content">{{ truncatedContent }}</div>
        </div>
      </div>
    </div>

    <!-- Composant chargé -->
    <BaseInputText
      v-if="isLoaded"
      ref="baseComponentRef"
      v-bind="$props"
      @update:model-value="$emit('update:modelValue', $event)"
      @click="$emit('click', $event)"
      @activate-group="$emit('activate-group', $event)"
      @deactivate-group="$emit('deactivate-group', $event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import BaseInputText from './BaseInputText.vue';

export default defineComponent({
  name: 'LazyBaseInputText',

  components: {
    BaseInputText,
  },

  props: {
    // Hériter de toutes les props de BaseInputText
    modelValue: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    maxLength: {
      type: Number,
      required: false,
      default: null,
    },
    // Props spécifiques au lazy loading
    loadMode: {
      type: String,
      required: false,
      default: 'auto',
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue', 'click', 'activate-group', 'deactivate-group'],

  setup(props) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    // Contenu tronqué pour l'aperçu
    const truncatedContent = computed(() => {
      if (!props.modelValue) return '';
      return props.modelValue.length > 50 ? `${props.modelValue.substring(0, 50)}...` : props.modelValue;
    });

    const loadComponent = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadComponent();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadComponent();
      } else if (props.loadMode === 'auto') {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    // Référence vers le composant chargé
    const baseComponentRef = ref();

    // Méthode focus qui redirige vers le composant chargé
    const focus = () => {
      if (isLoaded.value && baseComponentRef.value && baseComponentRef.value.focus) {
        baseComponentRef.value.focus();
      } else if (!isLoaded.value) {
        // Si pas encore chargé, charger d'abord puis focus
        loadComponent();
        nextTick(() => {
          if (baseComponentRef.value && baseComponentRef.value.focus) {
            baseComponentRef.value.focus();
          }
        });
      }
    };

    // Méthodes pour contrôler l'Intersection Observer pendant le scroll
    const pauseObserver = () => {
      if (observer) {
        observer.disconnect();
      }
    };

    const resumeObserver = () => {
      if (!isLoaded.value && containerRef.value) {
        setupIntersectionObserver();
      }
    };

    return {
      containerRef,
      isLoaded,
      loadComponent,
      truncatedContent,
      baseComponentRef,
      focus, // Exposer focus via return
      pauseObserver, // Exposer pour contrôler l'observer
      resumeObserver, // Exposer pour contrôler l'observer
    };
  },
});
</script>

<style scoped lang="scss">
.lazy-field-container {
  min-height: 40px;
  transition: all 0.3s ease;

  &.is-loading {
    border: 1px dashed #e0e0e0;
    border-radius: 4px;
  }

  &.is-loaded {
    border: none;
  }
}

.lazy-field-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0.5rem;
  cursor: pointer;
  background: #fafafa;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    border-color: #ccc;
  }

  .placeholder-content {
    text-align: center;
    max-width: 100%;
  }

  .placeholder-icon {
    font-size: 1.2rem;
    color: #999;
    margin-bottom: 0.25rem;
  }

  .placeholder-text {
    color: #666;
    font-size: 0.75rem;
    margin: 0 0 0.25rem 0;
    font-style: italic;
  }

  .placeholder-preview {
    max-width: 100%;
    margin-top: 0.25rem;
    
    .preview-content {
      background: white;
      border: 1px solid #ddd;
      border-radius: 3px;
      padding: 0.25rem 0.5rem;
      font-size: 0.7rem;
      color: #555;
      text-align: left;
      max-height: 30px;
      overflow: hidden;
      line-height: 1.2;
    }
  }
}
</style>
